// services/dashboard-api.js
import { authService } from './auth-service';
import { createLimiter, retryBusy } from '../utils/requestLimiter';

const biDetailLimit = createLimiter(3);

class DashboardAPI {
  async getDashboardSummary(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.include_declined) queryParams.append('include_declined', 'true');
    if (params.my_retailer_id) queryParams.append('my_retailer_id', params.my_retailer_id);

    const endpoint = `/dashboard/summary${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getCommissions(params = {}) {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    // Add parameters to query string
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    // Use the same pattern as other methods - let authService handle the base URL
    const endpoint = `/commissions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getCommissionDetail(commissionId) {
    const response = await authService.makeAuthenticatedRequest(`/commissions/${commissionId}`);
    return response.json();
  }

  async getStatusCounts() {
    const response = await authService.makeAuthenticatedRequest('/commissions/status-counts');
    return response.json();
  }

  async getTrends(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.granularity) queryParams.append('granularity', params.granularity);
    if (params.months) queryParams.append('months', params.months);
    if (params.my_retailer_id) queryParams.append('my_retailer_id', params.my_retailer_id);

    const endpoint = `/commissions/trends${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getTrafficStats(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.my_retailer_id) queryParams.append('my_retailer_id', params.my_retailer_id);

    const endpoint = `/traffic-stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getNetworkStats() {
    const response = await authService.makeAuthenticatedRequest('/network-stats');
    return response.json();
  }

  async getNetworkPartners() {
    const response = await authService.makeAuthenticatedRequest('/network-partners');
    return response.json();
  }

  async getPartnerProducts(partnerId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const qs = queryParams.toString();
    const endpoint = `/network-partners/${encodeURIComponent(partnerId)}/products${qs ? `?${qs}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getPartners(includeDeclined = false) {
    const queryParams = new URLSearchParams();
    queryParams.append('include_declined', includeDeclined.toString());
    
    const endpoint = `/partners?${queryParams.toString()}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getValidStatuses() {
    const response = await authService.makeAuthenticatedRequest('/commissions/valid-statuses');
    return response.json();
  }

  // NEW: Reporting API Methods

  async getAvailablePartnersForReporting() {
    const response = await authService.makeAuthenticatedRequest('/reporting/available-partners');
    return response.json();
  }

  async getMyRetailers() {
    const response = await authService.makeAuthenticatedRequest('/reporting/my-retailers');
    return response.json();
  }

  // Invoice API Methods

  async getInvoices(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const endpoint = `/invoices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async getInvoiceDetail(invoiceId, { testMode = false } = {}) {
    const params = testMode ? '?test_mode=true' : '';
    const response = await authService.makeAuthenticatedRequest(`/invoices/${invoiceId}${params}`);
    return response.json();
  }

  // The retailer's invoice to HandOff (self-billing), same shape as getInvoiceDetail.
  async getSelfBillDetail(selfBillId, { testMode = false } = {}) {
    const params = testMode ? '?test_mode=true' : '';
    const response = await authService.makeAuthenticatedRequest(`/self-bills/${selfBillId}${params}`);
    return response.json();
  }

  async downloadInvoicePdf(invoiceId, { testMode = false } = {}) {
    const params = testMode ? '?test_mode=true' : '';
    const response = await authService.makeAuthenticatedRequest(`/invoices/${invoiceId}/pdf${params}`);
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'PDF download failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="(.+?)"/);
    const filename = filenameMatch ? filenameMatch[1] : `invoice-${invoiceId}.pdf`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async downloadCommissionsExport(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const endpoint = `/commissions/export?${queryParams.toString()}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);

    if (!response.ok) {
      let body = {};
      try { body = await response.json(); } catch (_e) {}
      const err = new Error(body.error || 'Export failed');
      err.status = response.status;
      err.rowCount = body.row_count;
      err.maxRows = body.max_rows;
      err.hint = body.hint;
      throw err;
    }

    const rowCount = parseInt(response.headers.get('X-Row-Count') || '0', 10);
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="(.+?)"/);
    const ext = params.format === 'csv' ? 'csv' : 'xlsx';
    const filename = filenameMatch ? filenameMatch[1] : `handoff-orders.${ext}`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { rowCount, filename };
  }

  // Monthly statements, each with HandOff's invoice and the self-billed invoice.
  async getSettlements(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const endpoint = `/settlements${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  async downloadStatementPdf(settlementId, { testMode = false } = {}) {
    return this._downloadPdf(`/settlements/${settlementId}/pdf`, `statement-${settlementId}.pdf`, testMode);
  }

  async downloadSelfBillPdf(selfBillId, { testMode = false } = {}) {
    return this._downloadPdf(`/self-bills/${selfBillId}/pdf`, `self-bill-${selfBillId}.pdf`, testMode);
  }

  async _downloadPdf(path, fallbackName, testMode) {
    const params = testMode ? '?test_mode=true' : '';
    const response = await authService.makeAuthenticatedRequest(`${path}${params}`);
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'PDF download failed');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="(.+?)"/);
    const filename = filenameMatch ? filenameMatch[1] : fallbackName;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  async getOverdueInvoiceCount({ testMode = false } = {}) {
    const params = testMode ? '?test_mode=true' : '';
    const response = await authService.makeAuthenticatedRequest(`/invoices/overdue-count${params}`);
    return response.json();
  }

  // Approved orders for the next statement, both sides: { received: {count, amount}, sent: {count, amount} }.
  async getUninvoicedSummary() {
    const response = await authService.makeAuthenticatedRequest('/commissions/uninvoiced-summary');
    return response.json();
  }

  // Reporting API Methods

  async getPerformanceReport(params = {}) {
    /**
     * Generate performance report
     * @param {Object} params
     * @param {string|null} params.my_retailer_id - Specific retailer in user's group or null for all
     * @param {string|null} params.partner_retailer_id - Specific partner ID or null for all partners
     * @param {string} params.start_date - ISO date string
     * @param {string} params.end_date - ISO date string
     */
    const response = await authService.makeAuthenticatedRequest('/reporting/performance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        my_retailer_id: params.my_retailer_id || null,
        partner_retailer_id: params.partner_retailer_id || null,
        start_date: params.start_date,
        end_date: params.end_date,
      }),
    });
    return response.json();
  }
  async getMatchExceptions() {
    // Clicks & orders › Partner rules: what each rule did on this retailer's own pages in the last
    // 7 days, plus active balance limits (Phase 4b plan §2.6).
    const response = await authService.makeAuthenticatedRequest('/match-exceptions');
    return response.json();
  }

  // ---- Settings › Partner rules (Phase 4b plan §5). `retailerId` is only sent for a group user acting
  // for another retailer of its group. Every call returns `data` or throws the server's plain message.
  async _settingsRequest(path, { retailerId, method = 'GET', body, query = {} } = {}) {
    const qs = new URLSearchParams(query);
    if (retailerId) qs.set('retailer_id', retailerId);
    const endpoint = `${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
    const options = { method };
    if (body !== undefined) options.body = JSON.stringify(body);
    const response = await authService.makeAuthenticatedRequest(endpoint, options);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(json.error || 'Something went wrong. Try again.');
    return json.data;
  }

  // ---- Settings › AI assistants (spec buyer-insights-chatgpt-connector.md §6.3). createApiKey returns the
  // key once, in `key`.
  getApiKeys(retailerId) {
    return this._settingsRequest('/settings/api-keys', { retailerId });
  }

  createApiKey(client, retailerId) {
    return this._settingsRequest('/settings/api-keys', { retailerId, method: 'POST', body: { client } });
  }

  blockApiKey(keyId, retailerId) {
    return this._settingsRequest(`/settings/api-keys/${keyId}`, { retailerId, method: 'DELETE' });
  }

  // The brand with the most demand on the Monday list, for the tab's example questions.
  async getTopStillWantedBrand(retailerId) {
    const data = await this._settingsRequest('/reports/still-wanted', { retailerId, query: { top: 1 } });
    return data?.brands?.[0]?.brand || null;
  }

  getPartnerRules(retailerId) {
    return this._settingsRequest('/partner-rules', { retailerId });
  }

  createPartnerRule(body, retailerId) {
    return this._settingsRequest('/partner-rules', { retailerId, method: 'POST', body });
  }

  changePartnerRule(ruleId, body, retailerId) {
    return this._settingsRequest(`/partner-rules/${ruleId}`, { retailerId, method: 'PUT', body });
  }

  turnPartnerRule(ruleId, on, retailerId) {
    return this._settingsRequest(`/partner-rules/${ruleId}/${on ? 'on' : 'off'}`, { retailerId, method: 'POST' });
  }

  getPartnerRuleRegions(retailerId) {
    return this._settingsRequest('/partner-rules/regions', { retailerId });
  }

  getStores(retailerId) {
    return this._settingsRequest('/stores', { retailerId });
  }

  lookupStore(address, retailerId) {
    return this._settingsRequest('/stores/lookup', { retailerId, method: 'POST', body: address });
  }

  createStore(store, retailerId) {
    return this._settingsRequest('/stores', { retailerId, method: 'POST', body: store });
  }

  getTowns(lat, lon, km) {
    return this._settingsRequest('/towns', { query: { lat, lon, km } });
  }

  // ---- Settings › Visibility: the sale-filter switches (spec sale-filter-self-service.md).
  getSaleFilterSettings(retailerId) {
    return this._settingsRequest('/sale-filter-settings', { retailerId });
  }

  saveSaleFilterSettings(body, retailerId) {
    return this._settingsRequest('/sale-filter-settings', { retailerId, method: 'PUT', body });
  }

  async getBadgeFairness() {
    // Badge Fairness section (spec: sale-filter-dashboard-price-tag.md v2.0).
    // Returns {enabled: false} unless this retailer is in the rollout allowlist.
    const response = await authService.makeAuthenticatedRequest('/badge-fairness');
    return response.json();
  }

  // ---- Buyer Insights (spec: buyer-insights-dashboard-surfaces.md §2). Dates are YYYY-MM-DD strings.
  _biQuery(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, x));
      else qs.append(k, v);
    });
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  async getBuyerInsightsFilters(params = {}, { signal } = {}) {
    const response = await authService.makeAuthenticatedRequest(`/buyer-insights/filters${this._biQuery(params)}`, { signal });
    return response.json();
  }

  async getBuyerInsightsProducts(params = {}, { signal } = {}) {
    const response = await authService.makeAuthenticatedRequest(`/buyer-insights/products${this._biQuery(params)}`, { signal });
    return response.json();
  }

  // The retailer group's Buyer Insights settings (sold-out-at spec §5.4): today the discount cut points
  // {sold_out_at_bands: [20, 40]} that the Sold out at filter, the reports and the Claude connector use.
  async saveBuyerInsightsSettings(body = {}) {
    const response = await authService.makeAuthenticatedRequest('/buyer-insights/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  }

  // Per-product detail calls (size table, timeline) share one limiter: at most 3 in flight, the rest
  // queue; a 503 "busy" from the server is retried with backoff instead of shown as an error.
  _biDetail(endpoint, signal) {
    return biDetailLimit(async () => {
      const response = await retryBusy(() => authService.makeAuthenticatedRequest(endpoint, { signal }));
      return response.json();
    });
  }

  async getBuyerInsightsProductSizes(productId, params = {}, { signal } = {}) {
    return this._biDetail(`/buyer-insights/products/${encodeURIComponent(productId)}/sizes${this._biQuery(params)}`, signal);
  }

  async getBuyerInsightsProductTimeline(productId, params = {}, { signal } = {}) {
    return this._biDetail(`/buyer-insights/products/${encodeURIComponent(productId)}/timeline${this._biQuery(params)}`, signal);
  }

  async getBuyerInsightsBrands(params = {}, { signal } = {}) {
    const response = await authService.makeAuthenticatedRequest(`/buyer-insights/brands${this._biQuery(params)}`, { signal });
    return response.json();
  }

  // Second step of the Brands load: Thinner counts per brand and per view (the slow depth pass).
  async getBuyerInsightsBrandsDepth(params = {}, { signal } = {}) {
    const response = await authService.makeAuthenticatedRequest(`/buyer-insights/brands/depth${this._biQuery(params)}`, { signal });
    return response.json();
  }

  async getBuyerInsightsBrandDetail(name, params = {}, { signal } = {}) {
    return this._biDetail(`/buyer-insights/brands/detail${this._biQuery({ ...params, name })}`, signal);
  }

  async downloadBuyerInsightsCsv(params = {}) {
    return this._biDownload('/buyer-insights/products/export.csv', params, 'products');
  }

  async downloadBuyerInsightsBrandsCsv(params = {}) {
    return this._biDownload('/buyer-insights/brands/export.csv', params, 'brands');
  }

  async _biDownload(path, params, prefix) {
    const response = await authService.makeAuthenticatedRequest(`${path}${this._biQuery(params)}`);
    if (!response.ok) {
      let body = {};
      try { body = await response.json(); } catch (_e) {}
      const err = new Error(body.error || 'Export failed');
      err.status = response.status;
      err.rowCount = body.row_count;
      err.maxRows = body.max_rows;
      err.hint = body.hint;
      throw err;
    }
    const rowCount = parseInt(response.headers.get('X-Row-Count') || '0', 10);
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="(.+?)"/);
    const filename = filenameMatch ? filenameMatch[1] : `${prefix}-${params.view || 'all'}-${params.start}_${params.end}.csv`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { rowCount, filename };
  }

  async getPartnerDrilldown(partnerId, params = {}) {
    const queryParams = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
    );
    const qs = queryParams.toString();
    const endpoint = `/partner-drilldown/${encodeURIComponent(partnerId)}${qs ? `?${qs}` : ''}`;
    const response = await authService.makeAuthenticatedRequest(endpoint);
    return response.json();
  }

  // /connect page: Allow or Cancel for ChatGPT / Claude sign-in (spec buyer-insights-chatgpt-connector.md §4.2).
  // `query` is the OAuth authorize query string. Returns {redirect}; throws an Error with the server's `code`.
  async oauthConsent(query, allow) {
    const response = await authService.makeAuthenticatedRequest('/oauth/consent', {
      method: 'POST',
      body: JSON.stringify({ query, allow }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(json.error || 'Something went wrong.'), { code: json.error_code });
    return json;
  }
}

export const dashboardAPI = new DashboardAPI();